import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Check, Copy } from 'lucide-react';
import useClusterApiKey from '../../hooks/useClusterApiKey';

/**
 * W7 — GitOps steering-contract snippets (Karpenter panel).
 *
 * Renders the ready-to-paste Argo CD / Flux YAML from
 *   GET /v1/clusters/{id}/gitops-snippets   (gitops_routes.py, X-API-Key
 *   auth — same agent key resolved via useClusterApiKey)
 * so operators can exclude OptOps' Server-Side Apply steering writes
 * (fieldManager optops-steering) from GitOps drift correction.
 */

const YamlBlock = ({ label, text, copied, onCopy }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{label}</span>
      <Button
        size="sm"
        variant="outline"
        onClick={onCopy}
        className="inline-flex items-center gap-1"
      >
        {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </div>
    <pre className="text-[11px] font-mono whitespace-pre overflow-auto max-h-56 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
      {text}
    </pre>
  </div>
);

const GitOpsSnippetsModal = ({ open, onClose, clusterId }) => {
  const { apiKey, loading: keyLoading } = useClusterApiKey(clusterId);
  const [snippets, setSnippets] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null); // 'argocd' | 'flux' | null

  useEffect(() => {
    if (!open || !clusterId || !apiKey) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/v1/clusters/${clusterId}/gitops-snippets`,
          { headers: { 'X-API-Key': apiKey } },
        );
        if (!cancelled) {
          setSnippets(res.data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, clusterId, apiKey]);

  const handleCopy = async (which, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // clipboard unavailable (http origin) — operator can still select+copy
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>GitOps steering snippets</DialogTitle>
          <DialogDescription>
            OptOps steers NodePool / NodeClass fields via Server-Side Apply under
            fieldManager <code>{snippets?.field_manager || 'optops-steering'}</code>.
            Paste the snippet into the Argo CD Application or Flux Kustomization
            that manages your Karpenter manifests so drift correction leaves
            those fields alone.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p className="text-xs text-red-600">GitOps snippets API error: {error}</p>
        )}
        {!keyLoading && !apiKey && (
          <p className="text-xs text-gray-400">
            No active API key found for this cluster — snippets unavailable.
          </p>
        )}
        {!error && apiKey && !snippets && (
          <p className="text-xs text-gray-400">Loading snippets…</p>
        )}

        {snippets && (
          <div className="space-y-4">
            {/* Resources the snippets cover; empty = kind-wide fallback. */}
            <div className="flex flex-wrap items-center gap-1">
              {(snippets.pools || []).length === 0 ? (
                <span className="text-xs text-gray-500">
                  No Karpenter inventory yet — snippets cover all steered kinds.
                </span>
              ) : (
                snippets.pools.map((p) => (
                  <Badge key={`${p.kind}/${p.name}`} className="bg-gray-100 text-gray-700 text-xs">
                    {p.kind}/{p.name}
                  </Badge>
                ))
              )}
            </div>

            <YamlBlock
              label="Argo CD"
              text={snippets.argocd}
              copied={copied === 'argocd'}
              onCopy={() => handleCopy('argocd', snippets.argocd)}
            />
            <YamlBlock
              label="Flux"
              text={snippets.flux}
              copied={copied === 'flux'}
              onCopy={() => handleCopy('flux', snippets.flux)}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GitOpsSnippetsModal;
